import { createSelector } from 'reselect'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getSortedOrdersByDeparture } from 'selectors/orderSelectors'
import isUpcoming from 'lib/order/isUpcoming'
import { isOrderCancelled } from 'lib/order/orderUtils'
import { isSpoofed } from 'selectors/featuresSelectors'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import { isOrderInternationalTrip } from './getOrderIsLoyaltyInternationalTrip'
import { getOrderInternationalCountryCodes, isOrderPastTripEndForEsimDisplay } from 'loyalty/lib/getEligibleEsimOrders'
import { getCruiseItineraryCountryCodes } from 'checkout/lib/utils/cruises/itinerary'

/** Post-purchase benefits redeemable against a specific upcoming order. */
export type RedeemableBenefitCode = 'esim_package' | 'lounge_pass' | 'airport_transfer'

export interface NextOrderWithRedeemableBenefits {
  order: App.Order
  /** Coarsely-eligible benefits, in display order (eSIM, lounge, transfer). */
  benefits: Array<RedeemableBenefitCode>
  /** How many scanned upcoming orders are eligible — drives the 'View all bookings' CTA (shown when >1). */
  eligibleCount: number
}

/** Bounds the (synchronous, coarse) scan to the soonest upcoming trips. */
export const MAX_SCAN = 5

/**
 * Finds the soonest upcoming order that has at least one post-purchase-redeemable
 * Société benefit, used by the "Enjoy Société benefits on your upcoming escapes" card.
 *
 * Eligibility here is COARSE (synchronous): tier set + loyalty region + international +
 * the benefit existing in the account pool. eSIM is precise (its international check is
 * synchronous from the order payload). Lounge/transfer availability (airport experiences /
 * transfer options) is fetched per the chosen order and refined by the rendered rows, which
 * hide themselves when nothing is available.
 */
const getNextUpcomingOrderWithRedeemableBenefits = createSelector(
  getSortedOrdersByDeparture,
  (state: App.State) => state.loyalty.account.data?.benefits,
  (state: App.State) => getIsCurrentRegionUserLoyaltyRegion(state),
  (state: App.State) => getLoyaltyAccountRegion(state),
  (state: App.State) => getIsLoyaltyEsimEnabled(state),
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  (state: App.State) => state.loyalty.config?.transferAirports ?? EmptyArray,
  (state: App.State) => state.loyalty.config?.esimExcludedCountryCodes ?? EmptyArray,
  (state: App.State) => isSpoofed(state),
  (
    sortedOrders,
    benefits,
    isLoyaltyRegion,
    accountRegion,
    esimEnabled,
    flightDetails,
    cruiseSnapshots,
    transferAirports,
    esimExcludedCountryCodes,
    spoofed,
  ): NextOrderWithRedeemableBenefits | null => {
    if (!isLoyaltyRegion || !accountRegion) return null

    const hasBenefit = (code: RedeemableBenefitCode): boolean => {
      const benefit = benefits?.find(b => b.code === code)
      return !!benefit && (benefit.unlimited || !!benefit.remaining)
    }
    const hasLounge = hasBenefit('lounge_pass')
    const hasTransfer = hasBenefit('airport_transfer')
    const hasEsim = esimEnabled && hasBenefit('esim_package')

    if (!hasLounge && !hasTransfer && !hasEsim) return null

    const candidates = sortedOrders.data
      .filter(order =>
        !isOrderCancelled(order) &&
        isUpcoming(order, spoofed) &&
        order.isBookingOwner &&
        !!order.loyaltyDetails.tier,
      )
      .slice(0, MAX_SCAN)

    // Walk in departure order: the first eligible order is the one we render (nearest departure),
    // and the total eligible count drives the 'View all bookings' CTA.
    let firstOrder: App.Order | undefined
    let firstBenefits: Array<RedeemableBenefitCode> = []
    let eligibleCount = 0

    for (const order of candidates) {
      const orderBenefits: Array<RedeemableBenefitCode> = []
      const isInternational = isOrderInternationalTrip(order, flightDetails, cruiseSnapshots)

      if (hasEsim && !isOrderPastTripEndForEsimDisplay(order)) {
        // Cruise port/itinerary country codes aren't on the order payload — derive them from the
        // offer snapshot (mirrors getOrderIsEsimInternationalTrip) and pass flightDetails so the
        // helper resolves flight destinations too. Without this, eSIM never qualifies for a
        // cruise/flight order in this section even though the order page shows it.
        const cruiseCountryCodes = order.cruiseItems.flatMap(item => {
          if (item.status === 'cancelled') return []
          const snapshot = cruiseSnapshots[item.bookingId]
          return snapshot ? getCruiseItineraryCountryCodes(snapshot) : []
        })
        const esimCountryCodes = getOrderInternationalCountryCodes(order, accountRegion, flightDetails, cruiseCountryCodes, esimExcludedCountryCodes)
        if (esimCountryCodes.length > 0) orderBenefits.push('esim_package')
      }
      if (hasLounge && isInternational) orderBenefits.push('lounge_pass')
      if (hasTransfer && isInternational && transferAirports.length > 0) orderBenefits.push('airport_transfer')

      if (orderBenefits.length) {
        eligibleCount += 1
        if (!firstOrder) {
          firstOrder = order
          firstBenefits = orderBenefits
        }
      }
    }

    if (!firstOrder) return null

    return { order: firstOrder, benefits: firstBenefits, eligibleCount }
  },
)

export default getNextUpcomingOrderWithRedeemableBenefits
